// Searches — TODO T30 (1 Sep 2026); the calls are T17's and T18's.
//
// The brain's organizing unit and home screen (CLAUDE.md § Brain tier, Mona's Q2 answer of
// 23 Aug 2026), so this is the busiest domain module: the mandate, its candidate list, the stage
// each candidate holds IN THIS SEARCH, and who is working it.
//
// THE STAGE IS PER (SEARCH, CANDIDATE). `setStage` names both, always — T4 keyed
// `pipeline_entries` that way because a candidate sits at a different stage in each mandate, so
// there is no stage on `candidates` to set instead.
import { useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  BriefSuggestion,
  BriefSuggestionsResponse,
  KnownAnswer,
  NotAFitOutcome,
  PipelineStage,
  SearchCandidate,
  SearchDetail,
  SearchFeedbackResponse,
  SearchStatus,
  SearchSummary,
  SuggestedResponse,
} from '@vera/bff';
import type { RoleFamily } from '@vera/db/questions';
import type { Requirement } from '@vera/db/requirements';
import type { PeerCompany } from '@vera/db/peer-companies';
import type { Wanted } from '@vera/db/traits';
import type { RolesPlan } from '../roles.ts';
import { API_PREFIX, type RequestFn } from './core.ts';
import { POLL_MS } from './intervals.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

/**
 * What the form sends. The service is the authority on what it accepts
 * (services/bff/src/body.ts); this says what this app sends.
 */
export interface SearchInput {
  title: string;
  clientId: string | null;
  roleBrief: string | null;
  pitchContext: string | null;
  location: string | null;
  feeNotes: string | null;
  /** ISO 8601, or null. */
  deadline: string | null;
  /** T31 (2 Sep 2026), replacing `active`. */
  status: SearchStatus;
  sector: string | null;
  leadStaffId: string | null;
  /** T51 (9 Sep 2026): kadmon describes the role and never names the company. */
  confidential: boolean;
  /**
   * T177 (30 Sep 2026): what the team has told the agents about the Search, sent whole. Only the
   * Search page's list sends it; the form leaves it out, so an edit never erases it.
   */
  known?: KnownAnswer[];
  /** T202 S-1: questions of kadmon's whose answers the page takes off the Search (their ids). */
  dropAnswers?: string[];
  /**
   * T53e (30 Sep 2026): what the Search needs, sent whole by the Requirements card and by the New
   * search form when it is filled; the Edit form leaves them out, so an edit never erases them.
   * Unknown-shaped here: the service checks them (`@vera/db/requirements`) and names each refusal.
   */
  family?: RoleFamily | null;
  requirements?: readonly Record<string, unknown>[];
  /** T141b: the requirements the card was opened on; a Save on a list since changed is refused. */
  requirementsSeen?: readonly Requirement[];
  /**
   * T123a's second PR: the client's stated requirements this save applies (`BriefSuggestion`),
   * filed as applied with it, so each leaves the card or the New search form.
   */
  fromFacts?: readonly string[];
  payBand?: Record<string, unknown> | null;
  /** T124 (1 Oct 2026): the traits the role wants, sent whole by the "What the role wants" card. */
  wanted?: Wanted;
  /** T405 (19 Oct 2026): the companies to look in, sent whole by the Companies card. */
  peerCompanies?: readonly PeerCompany[];
}

/** T405: what Suggest comes back with: the companies kept, and those code took out with why. */
export interface PeerSuggestion {
  companies: PeerCompany[];
  removed: { name: string; why: string }[];
}

export const searchesApi = (request: RequestFn) => ({
  list: async () =>
    (await request<{ searches: SearchSummary[] }>(`${API_PREFIX}/searches`)).searches,
  get: (searchId: string) => request<SearchDetail>(`${API_PREFIX}/searches/${searchId}`),
  /** T124: Suggested for this Search, the recipe's order of the people on file not on it. */
  suggested: (searchId: string) =>
    request<SuggestedResponse>(`${API_PREFIX}/searches/${searchId}/suggested`),
  listCandidates: async (searchId: string) =>
    (
      await request<{ candidates: SearchCandidate[] }>(
        `${API_PREFIX}/searches/${searchId}/candidates`,
      )
    ).candidates,

  create: async (input: SearchInput) =>
    (await request<{ id: string }>(`${API_PREFIX}/searches`, { method: 'POST', body: input })).id,
  /** T49: gone for good, with its placements; refused while a thread, a code or a put-forward is on it. */
  remove: (searchId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}`, { method: 'DELETE' }),
  /** T123a's second PR: what a client's people said a new role needs, for the New search form. */
  briefSuggestions: async (clientId: string) =>
    (await request<BriefSuggestionsResponse>(`${API_PREFIX}/clients/${clientId}/brief-suggestions`))
      .suggestions,
  /** T123a's second PR: one of them, or one on a Search's card, dismissed for good. */
  dismissBrief: (factId: string) =>
    request<void>(`${API_PREFIX}/brief-suggestions/${factId}/dismiss`, { method: 'POST' }),
  /** T405: up to twelve companies like the client. Nothing is saved. */
  suggestPeers: (searchId: string) =>
    request<PeerSuggestion>(`${API_PREFIX}/searches/${searchId}/peer-companies/suggest`, {
      method: 'POST',
    }),
  update: (searchId: string, input: Partial<SearchInput>) =>
    request<void>(`${API_PREFIX}/searches/${searchId}`, { method: 'PATCH', body: input }),
  /** T141: `from: 'suggested'` keeps Add to this Search from Suggested as the team's yes. */
  setStage: (
    searchId: string,
    candidateId: string,
    stage: PipelineStage | undefined,
    from?: 'suggested',
    /** T365b: the role's own stage, or null for the main stage; absent keeps the one they are in. */
    step?: string | null,
  ) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/candidates/${candidateId}`, {
      method: 'PUT',
      body: {
        ...(stage ? { stage } : {}),
        ...(from ? { from } : {}),
        ...(step !== undefined ? { step } : {}),
      },
    }),
  /** T365b: a role's own stages. */
  addStep: (searchId: string, step: { label: string; stage: PipelineStage }) =>
    request<{ id: string }>(`${API_PREFIX}/searches/${searchId}/steps`, {
      method: 'POST',
      body: step,
    }),
  changeStep: (
    searchId: string,
    stepId: string,
    change: { label?: string; stage?: PipelineStage; position?: number },
  ) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/steps/${stepId}`, {
      method: 'PATCH',
      body: change,
    }),
  dropStep: (searchId: string, stepId: string) =>
    request<{ moved: number }>(`${API_PREFIX}/searches/${searchId}/steps/${stepId}`, {
      method: 'DELETE',
    }),
  /** T141: a Search's rules from Not a fit's reasons, and who stands marked not a fit. */
  feedback: (searchId: string) =>
    request<SearchFeedbackResponse>(`${API_PREFIX}/searches/${searchId}/feedback`),
  /** T141: Not a fit on a person Suggested shows, with the reason kadmon reads for a rule. */
  notAFit: (searchId: string, candidateId: string, reason: string | null) =>
    request<NotAFitOutcome>(
      `${API_PREFIX}/searches/${searchId}/suggested/${candidateId}/not-a-fit`,
      { method: 'POST', body: reason ? { reason } : {} },
    ),
  undoNotAFit: (searchId: string, feedbackId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/not-a-fit/${feedbackId}/undo`, {
      method: 'POST',
    }),
  /** T141a: More like this (`like`) or Less like this (`unlike`) on a person for a Search. */
  markAnchor: (searchId: string, candidateId: string, verdict: 'like' | 'unlike') =>
    request<{ feedbackId: string; existing: boolean }>(
      `${API_PREFIX}/searches/${searchId}/people/${candidateId}/anchor`,
      { method: 'POST', body: { verdict } },
    ),
  undoAnchor: (searchId: string, feedbackId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/anchors/${feedbackId}/undo`, {
      method: 'POST',
    }),
  decideRule: (searchId: string, feedbackId: string, decision: 'add' | 'decline') =>
    request<void>(`${API_PREFIX}/searches/${searchId}/rules/${feedbackId}`, {
      method: 'POST',
      body: { decision },
    }),
  /** T431d: Undo on a line a client's words landed on the Search; the line before comes back. */
  undoRequirementLanding: (searchId: string, landingId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/requirement-landings/${landingId}/undo`, {
      method: 'POST',
    }),
  deleteRule: (searchId: string, feedbackId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/rules/${feedbackId}`, { method: 'DELETE' }),
  /**
   * T98: "Client passed" on a person's entry, and its undo. kadmon reads it, tells the person once on
   * their next message, and may then look for another Search for them (the matching design § 7).
   */
  setClientPassed: (searchId: string, candidateId: string, passed: boolean) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/candidates/${candidateId}/client-passed`, {
      method: 'PUT',
      body: { passed },
    }),
  /**
   * T177c: a client interview the team booked, as a wall-clock time ("2026-10-16T14:00") and the
   * zone it was entered in. `label` is the team's own and no agent reads it.
   */
  bookInterview: (searchId: string, candidateId: string, input: InterviewInput) =>
    request<{ id: string }>(
      `${API_PREFIX}/searches/${searchId}/candidates/${candidateId}/interviews`,
      { method: 'POST', body: input },
    ),
  moveInterview: (interviewId: string, input: InterviewInput) =>
    request<void>(`${API_PREFIX}/client-interviews/${interviewId}`, { method: 'PUT', body: input }),
  /** T177k: a cancel may name whom kadmon tells; with the candidate, whether a new time follows. */
  setInterviewCancelled: (interviewId: string, cancelled: boolean, tell?: InterviewTell) =>
    request<void>(`${API_PREFIX}/client-interviews/${interviewId}/cancelled`, {
      method: 'PUT',
      body: {
        cancelled,
        ...(tell ? { tell: tell.to } : {}),
        ...(tell?.to === 'candidate' && tell.newTime ? { newTime: true } : {}),
      },
    }),
  removeCandidate: (searchId: string, candidateId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/candidates/${candidateId}`, {
      method: 'DELETE',
    }),
  assignStaff: (searchId: string, staffId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/assignments/${staffId}`, { method: 'PUT' }),
  unassignStaff: (searchId: string, staffId: string) =>
    request<void>(`${API_PREFIX}/searches/${searchId}/assignments/${staffId}`, {
      method: 'DELETE',
    }),
});

export function useSearches(): UseQueryResult<SearchSummary[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['searches'],
    queryFn: () => searchesApi(request).list(),
    refetchInterval: POLL_MS.searches,
  });
}

export function useSearch(searchId: string): UseQueryResult<SearchDetail> {
  const request = useRequest();
  return useQuery({
    queryKey: ['search', searchId],
    queryFn: () => searchesApi(request).get(searchId),
    refetchInterval: POLL_MS.searches,
  });
}

export function useSearchCandidates(searchId: string): UseQueryResult<SearchCandidate[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['search', searchId, 'candidates'],
    queryFn: () => searchesApi(request).listCandidates(searchId),
    refetchInterval: POLL_MS.candidates,
  });
}

/**
 * T124 (1 Oct 2026): Suggested for this Search. Computed by the service when asked, so polled at the
 * slow pace: it moves when a person's answers, a profile or the Search changes, and a write here
 * (adding someone, an edit of what the Search needs) refetches it at once through `KEYS.search`.
 */
export function useSuggested(searchId: string, enabled = true): UseQueryResult<SuggestedResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['search', searchId, 'suggested'],
    queryFn: () => searchesApi(request).suggested(searchId),
    refetchInterval: POLL_MS.slow,
    enabled,
  });
}

/**
 * T123a's second PR: what a client's people said a new role needs, once the New search form has
 * that client picked. Under `search`, so creating the Search refetches it.
 */
export function useClientBriefSuggestions(clientId: string): UseQueryResult<BriefSuggestion[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['search', 'client-brief', clientId],
    queryFn: () => searchesApi(request).briefSuggestions(clientId),
    enabled: clientId !== '',
  });
}

export const useDismissBriefSuggestion = () =>
  useConsoleMutation(
    (request, factId: string) => searchesApi(request).dismissBrief(factId),
    KEYS.search,
  );

// One hook per action rather than a generic one at each call site, so the invalidation for an
// action is decided once and a screen cannot forget it.

export const useCreateSearch = () =>
  useConsoleMutation(
    (request, input: SearchInput) => searchesApi(request).create(input),
    KEYS.search,
  );

export const useUpdateSearch = () =>
  useConsoleMutation(
    (request, args: { searchId: string; input: Partial<SearchInput> }) =>
      searchesApi(request).update(args.searchId, args.input),
    KEYS.search,
  );

/** T405: Suggest on the Companies card. Saves nothing, so nothing is refetched. */
export const useSuggestPeers = () =>
  useConsoleMutation(
    (request, searchId: string) => searchesApi(request).suggestPeers(searchId),
    [],
  );

export const useDeleteSearch = () =>
  useConsoleMutation(
    (request, searchId: string) => searchesApi(request).remove(searchId),
    KEYS.search,
  );

export const useSetClientPassed = () =>
  useConsoleMutation(
    (request, args: { searchId: string; candidateId: string; passed: boolean }) =>
      searchesApi(request).setClientPassed(args.searchId, args.candidateId, args.passed),
    KEYS.search,
  );

/** T177c: what the Add interview and Move forms send. */
export interface InterviewInput {
  local: string;
  zone: string;
  /** Always sent: empty clears the label on a move, and means none on a booking. */
  label: string;
}

/** T177k: whom a team's cancel asks kadmon to tell; absent, nobody. */
export interface InterviewTell {
  to: 'candidate' | 'client';
  /** With the candidate: they'd like a new time ("a new date may follow"). */
  newTime?: boolean;
}

export const useBookInterview = () =>
  useConsoleMutation(
    (request, args: { searchId: string; candidateId: string; input: InterviewInput }) =>
      searchesApi(request).bookInterview(args.searchId, args.candidateId, args.input),
    KEYS.search,
  );

export const useMoveInterview = () =>
  useConsoleMutation(
    (request, args: { interviewId: string; input: InterviewInput }) =>
      searchesApi(request).moveInterview(args.interviewId, args.input),
    KEYS.search,
  );

export const useSetInterviewCancelled = () =>
  useConsoleMutation(
    (request, args: { interviewId: string; cancelled: boolean; tell?: InterviewTell }) =>
      searchesApi(request).setInterviewCancelled(args.interviewId, args.cancelled, args.tell),
    KEYS.search,
  );

export const useSetStage = () =>
  useConsoleMutation(
    (
      request,
      args: {
        searchId: string;
        candidateId: string;
        stage?: PipelineStage;
        from?: 'suggested';
        step?: string | null;
      },
    ) =>
      searchesApi(request).setStage(
        args.searchId,
        args.candidateId,
        args.stage,
        args.from,
        args.step,
      ),
    KEYS.search,
  );

/** T365b: add, rename or move, and drop one of a role's own stages; the page refetches. */
export const useAddStep = () =>
  useConsoleMutation(
    (request, args: { searchId: string; label: string; stage: PipelineStage }) =>
      searchesApi(request).addStep(args.searchId, { label: args.label, stage: args.stage }),
    KEYS.search,
  );

export const useChangeStep = () =>
  useConsoleMutation(
    (
      request,
      args: {
        searchId: string;
        stepId: string;
        change: { label?: string; stage?: PipelineStage; position?: number };
      },
    ) => searchesApi(request).changeStep(args.searchId, args.stepId, args.change),
    KEYS.search,
  );

export const useDropStep = () =>
  useConsoleMutation(
    (request, args: { searchId: string; stepId: string }) =>
      searchesApi(request).dropStep(args.searchId, args.stepId),
    KEYS.search,
  );

/**
 * T141 (3 Oct 2026): a Search's rules and standing marks. A write here refetches it through
 * `KEYS.search`, as Suggested is; otherwise polled at the slow pace, as Suggested is.
 */
export function useSearchFeedback(searchId: string): UseQueryResult<SearchFeedbackResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['search', searchId, 'feedback'],
    queryFn: () => searchesApi(request).feedback(searchId),
    refetchInterval: POLL_MS.slow,
  });
}

export const useNotAFit = () =>
  useConsoleMutation(
    (request, args: { searchId: string; candidateId: string; reason: string | null }) =>
      searchesApi(request).notAFit(args.searchId, args.candidateId, args.reason),
    KEYS.search,
  );

export const useUndoNotAFit = () =>
  useConsoleMutation(
    (request, args: { searchId: string; feedbackId: string }) =>
      searchesApi(request).undoNotAFit(args.searchId, args.feedbackId),
    KEYS.search,
  );

export const useMarkAnchor = () =>
  useConsoleMutation(
    (request, args: { searchId: string; candidateId: string; verdict: 'like' | 'unlike' }) =>
      searchesApi(request).markAnchor(args.searchId, args.candidateId, args.verdict),
    KEYS.search,
  );

export const useUndoAnchor = () =>
  useConsoleMutation(
    (request, args: { searchId: string; feedbackId: string }) =>
      searchesApi(request).undoAnchor(args.searchId, args.feedbackId),
    KEYS.search,
  );

export const useDecideRule = () =>
  useConsoleMutation(
    (request, args: { searchId: string; feedbackId: string; decision: 'add' | 'decline' }) =>
      searchesApi(request).decideRule(args.searchId, args.feedbackId, args.decision),
    KEYS.search,
  );

export const useUndoRequirementLanding = () =>
  useConsoleMutation(
    (request, args: { searchId: string; landingId: string }) =>
      searchesApi(request).undoRequirementLanding(args.searchId, args.landingId),
    KEYS.search,
  );

export const useDeleteRule = () =>
  useConsoleMutation(
    (request, args: { searchId: string; feedbackId: string }) =>
      searchesApi(request).deleteRule(args.searchId, args.feedbackId),
    KEYS.search,
  );

/**
 * What the roles picker planned, carried out — T38b (16 Sep 2026). The roles on file first, then
 * each new role made and the person put on it, one write after another: a refusal stops with its
 * sentence and what landed stays landed, and a second press finds a role made the first time by
 * its name (src/roles.ts) instead of making it twice. A new role is the title on the company and
 * nothing else — the rest is filled in on its page — so it opens at the service's defaults.
 */
export function useApplyRoles() {
  const create = useConsoleMutation(
    (request, role: { clientId: string; title: string }) =>
      searchesApi(request).create({
        title: role.title,
        clientId: role.clientId,
        roleBrief: null,
        pitchContext: null,
        location: null,
        feeNotes: null,
        deadline: null,
        status: 'active',
        sector: null,
        leadStaffId: null,
        confidential: false,
      }),
    // A company's count of roles, on its tab's board, moves with it.
    [...KEYS.search, ['clients'], ['client']],
  );
  const setStage = useSetStage();
  const [busy, setBusy] = useState(false);

  const apply = async (plan: RolesPlan, candidateId: string) => {
    setBusy(true);
    try {
      for (const searchId of plan.add) {
        await setStage.mutateAsync({ searchId, candidateId, stage: 'sourced' });
      }
      for (const role of plan.create) {
        const searchId = await create.mutateAsync(role);
        await setStage.mutateAsync({ searchId, candidateId, stage: 'sourced' });
      }
    } finally {
      setBusy(false);
    }
  };
  return { apply, busy, error: create.error ?? setStage.error };
}

export const useRemoveFromSearch = () =>
  useConsoleMutation(
    (request, args: { searchId: string; candidateId: string }) =>
      searchesApi(request).removeCandidate(args.searchId, args.candidateId),
    KEYS.search,
  );

export const useAssignStaff = () =>
  useConsoleMutation(
    (request, args: { searchId: string; staffId: string; assigned: boolean }) =>
      args.assigned
        ? searchesApi(request).assignStaff(args.searchId, args.staffId)
        : searchesApi(request).unassignStaff(args.searchId, args.staffId),
    KEYS.search,
  );
